import { Box, Button, Chip, Divider, Grid } from "@mui/material";
import { grey } from "@mui/material/colors";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { QuickSendErrorContext } from "contexts/QuickSendError";
import { Dispatch, SetStateAction, useContext } from "react";
import { PreviewMessage } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import {
  Template,
  TemplateContentType,
} from "features/Influencer/ContactList/schema";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "./styles";

interface IProps {
  emailMessages: PreviewMessage[];
  setEmailMessages: Dispatch<SetStateAction<PreviewMessage[]>>;
  draftTab: number;
  setDraftTab: Dispatch<SetStateAction<number>>;
  setSelectedTemplates: Dispatch<
    SetStateAction<{ [key in number]: Template | null }>
  >;
}

export default function SelectDraftsTab({
  emailMessages,
  setEmailMessages,
  draftTab,
  setDraftTab,
  setSelectedTemplates,
}: IProps) {
  const followups = emailMessages.slice(1, emailMessages?.length);
  const { followupTemplates } = useContext(OutreachTemplatesContext);
  const { errors } = useContext(QuickSendErrorContext);

  const handleAddMoreFollowup = () => {
    if (followups?.length === MAXIMUM_FOLLOWUP_ALLOWED) return;
    trackEvent("Add Another Follow Up Button Clicked", {
      Source: "Compose Email",
    });

    setEmailMessages((prev) => {
      const copy = makeDeepCopy(prev);
      if (followupTemplates?.length === 0) {
        copy.push({
          name: "Followup Default",
          body: "",
          subject: "Followup Default",
          contentType: TemplateContentType.FOLLOWUP,
        });
      } else {
        const newTemplate = followupTemplates[0];
        copy.push(newTemplate);
        const newLength = copy.length;
        setSelectedTemplates((prev) => {
          const selectedCopy = { ...prev };
          selectedCopy[newLength - 1] = newTemplate;
          return selectedCopy;
        });
      }
      return copy;
    });
  };

  const handleRemoveFollowUp = (index: number) => {
    trackEvent("Remove Follow Up Button Clicked", {
      "Follow Up Position": index,
      Source: "Compose Email",
    });
    setEmailMessages((prev: PreviewMessage[]) => {
      const copy = makeDeepCopy(prev);
      copy.splice(index, 1);
      return copy;
    });
    if (draftTab >= index) {
      setDraftTab((prev) => prev - 1);
    }
  };

  const switchToOutreach = (e: CustomEvent) => {
    e.preventDefault();
    setDraftTab(0);
  };

  const switchToFollowUp = (e: CustomEvent, index: number) => {
    e.preventDefault();
    setDraftTab(index);
  };

  return (
    <>
      <Grid container alignItems="end">
        <Chip
          label="Email"
          size="small"
          sx={[
            draftTab === 0 ? styles.active : styles.inactive,
            errors.outreachBody === 0 && draftTab !== 0 && styles.error,
          ]}
          onClick={switchToOutreach}
        />

        {followups?.map((x, index) => (
          <Chip
            key={index}
            label={`Follow-Up ${index + 1}`}
            size="small"
            sx={[
              draftTab === index + 1 ? styles.active : styles.inactive,
              errors.outreachBody === index + 1 &&
                draftTab !== index + 1 &&
                styles.error,
            ]}
            onClick={(e) => switchToFollowUp(e, index + 1)}
            onDelete={() => handleRemoveFollowUp(index + 1)}
            deleteIcon={<Box component="i" className="fa-thin fa-xmark" />}
          />
        ))}

        {followups?.length < MAXIMUM_FOLLOWUP_ALLOWED && (
          <Button
            size="small"
            sx={styles.addButton}
            onClick={handleAddMoreFollowup}
          >
            {followups?.length > 0 ? "Add + " : "Add Follow-Up +"}
          </Button>
        )}
      </Grid>
      <Divider sx={{ mt: -0.1, backgroundColor: grey[200] }} />
    </>
  );
}
